import { type RangePickerProps } from 'antd/es/date-picker';
import { type ConfigType, type Dayjs } from 'dayjs';
import { DefaultDateFormat, ISO8601DateFormat } from 'lib/moment/constants';
import { dayjs } from 'lib/moment/dayjsConfig';
import { type ObjectType } from 'shared/types';

export const convertMillisecondsToDays = (milliseconds: number) =>
  milliseconds / (1000 * 60 * 60 * 24);

export const formatDisplayDate = (
  date: ConfigType,
  config?: {
    format?: string;
    timeZone?: string | null;
  }
) => {
  if (!date) return '-';

  const { format, timeZone } = config || {};

  return dayjs(date)
    .tz(timeZone || undefined)
    .format(format ?? 'MMM DD, YYYY');
};

export const formatDisplayTime = (
  dateTime: ConfigType,
  config?: {
    format?: string;
    timeZone?: string | null;
  }
) => {
  if (!dateTime) return '-';

  const { format, timeZone } = config || {};

  return dayjs(dateTime)
    .tz(timeZone || undefined)
    .format(format ?? 'h:mm A');
};

export const formatDisplayTimeAgo = (date: ConfigType, withSuffix?: boolean) =>
  date ? dayjs(date).fromNow(withSuffix) : '-';

export const getTimeDuration = (timeInMinute: number) => {
  const hours = Math.floor(timeInMinute / 60);
  const minutes = timeInMinute % 60;

  return `${hours}h${minutes}m`;
};

export const disabledPastDate: RangePickerProps['disabledDate'] = (
  day: Dayjs
) => day.isBefore(dayjs(), 'day');

export const disabledFutureDate: RangePickerProps['disabledDate'] = (
  day: Dayjs
) => day.isAfter(dayjs(), 'day');

export type TDisabledDate = (current: Dayjs) => boolean;

/**
 * Disable future dates relative to `toDate` (i.e. dates after toDate).
 * Used for `FromDate` in a FromDate - ToDate pair.
 */
export const disabledFromDate =
  (toDate?: Dayjs | null): TDisabledDate =>
  (current: Dayjs) => {
    if (!toDate) return false;
    return Boolean(current && current.isAfter(toDate.endOf('day')));
  };

/**
 * Disable past dates relative to `fromDate` (i.e. dates before fromDate).
 * Used for `ToDate` in a FromDate - ToDate pair.
 */
export const disabledToDate =
  (fromDate?: Dayjs | null): TDisabledDate =>
  (current: Dayjs) => {
    if (!fromDate) return false;
    return Boolean(current && current.isBefore(fromDate.startOf('day')));
  };

/** Convert date in string YYYY-MM-DD (ISO8601) to Dayjs object if found in parsed query params */
export const convertDateStringToObjectDate = (obj: ObjectType): ObjectType => {
  const isValidDateString = (value: unknown): value is string => {
    const iso8601DateFormatRegexPattern = /^\d{4}-\d{2}-\d{2}$/;
    return (
      typeof value === 'string' && iso8601DateFormatRegexPattern.test(value)
    );
  };

  const convertValue = (value: unknown): unknown => {
    if (isValidDateString(value)) {
      return dayjs(value, ISO8601DateFormat);
    }
    if (Array.isArray(value)) {
      return value.map(v =>
        isValidDateString(v) ? dayjs(v, ISO8601DateFormat) : v
      );
    }
    return value;
  };
  return Object.entries(obj).reduce<ObjectType>((result, [key, value]) => {
    result[key] = convertValue(value);
    return result;
  }, {});
};

export const formatDateRange = (dateRange?: [string?, string?]) => {
  if (!dateRange) return {};
  const [from, to] = dateRange;
  return { from, to };
};

export const formatDateRangeForExport = (
  dateRange: [string?, string?] | string[] | undefined | null
) => {
  if (!dateRange || !Array.isArray(dateRange) || dateRange.length !== 2)
    return {};

  const [from, to] = dateRange;
  return {
    from: from
      ? dayjs(from, DefaultDateFormat).format(ISO8601DateFormat)
      : undefined,
    to: to ? dayjs(to, DefaultDateFormat).format(ISO8601DateFormat) : undefined,
  };
};

// ---------------------------------------------------------------------------
// Helper – expand a [start, end] range into individual weekday Dayjs objects
// ---------------------------------------------------------------------------
export function getWeekdaysInRange(start: Dayjs, end: Dayjs): Dayjs[] {
  const days: Dayjs[] = [];
  let current = start.startOf('day');
  const last = end.startOf('day');

  while (!current.isAfter(last)) {
    const dow = current.day(); // 0 = Sunday, 6 = Saturday
    if (dow !== 0 && dow !== 6) {
      days.push(current);
    }
    current = current.add(1, 'day');
  }

  return days;
}

// ---------------------------------------------------------------------------
// Helper – expand a [start, end] range into ALL days (including Sat & Sun)
// ---------------------------------------------------------------------------
export function getAllDaysInRange(start: Dayjs, end: Dayjs): Dayjs[] {
  const days: Dayjs[] = [];
  let current = start.startOf('day');
  const last = end.startOf('day');

  while (!current.isAfter(last)) {
    days.push(current);
    current = current.add(1, 'day');
  }

  return days;
}
